import { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import { UseCourseFansTableColumnsProps } from '../types/courseAnalytics.types';
import { StanFloatingMenu, StanTag } from '@/components';
import { copyToClipboard, STAN_TAG_PRESETS, stanNotify } from '@/lib';
import { LuEllipsisVertical } from 'react-icons/lu';
import {
    useGenerateCourseLink,
    useRestoreCourseAccess,
    useRevokeCourseAccess,
    useSendCourseLink,
} from '@/queries/useCourses';
import { CourseFan } from '@/types/courses';

export const useCourseFansTableColumns = ({
    showEmailOptions,
    courseId,
    onRefresh,
}: UseCourseFansTableColumnsProps): StanColumnDef<CourseFan>[] => {
    const { mutate: revokeCourseAccess } = useRevokeCourseAccess(courseId);
    const { mutate: restoreCourseAccess } = useRestoreCourseAccess(courseId);
    const { mutate: sendCourseLink } = useSendCourseLink();
    const { mutate: generateCourseLink } = useGenerateCourseLink();

    if (showEmailOptions) {
        return [
            { header: 'Name', accessorKey: 'fullName', width: '40%' },
            { header: 'Email', accessorKey: 'email', width: '40%' },
        ];
    }

    return [
        { header: 'Name', accessorKey: 'fullName', width: '20%' },
        { header: 'Email', accessorKey: 'email', width: '20%', hideOnMobile: true },
        { header: 'Start Date', accessorKey: 'fanSinceDate', width: '20%', hideOnMobile: true },
        {
            header: 'Status',
            accessorKey: 'percentCompleteString',
            width: '50%',
            cell: info => (
                <div className='flex w-full flex-col gap-2 md:flex-row md:items-center'>
                    <div className='flex items-center gap-1.5'>
                        <div className='min-w-28'>
                            <StanTag
                                label={info.row.original.statusText}
                                style={{
                                    backgroundColor:
                                        STAN_TAG_PRESETS.courseProgressStatus?.[info.row.original.statusText]
                                            ?.backgroundColor,
                                    color: STAN_TAG_PRESETS.courseProgressStatus?.[info.row.original.statusText]
                                        ?.textColor,
                                }}
                            />
                        </div>

                        <span className='text-label-sm-bold text-dark'>{info.row.original.percentCompleteString}</span>
                    </div>
                    <div className='bg-neutral-subtle h-1.5 w-full overflow-hidden rounded-full md:min-w-28'>
                        <div
                            className='bg-brand-main h-full rounded-full transition-all'
                            style={{ width: `${info.row.original.percent_complete}%` }}
                        />
                    </div>
                </div>
            ),
        },
        {
            header: '',
            accessorKey: 'actions',
            width: '10%',
            // hideOnMobile: true,
            cell: info => {
                const fan = info.row.original;
                const isActive = fan.status === 1;
                const isRevoked = fan.status === 2;

                if (!isActive && !isRevoked) return null;

                return (
                    <div className='flex justify-end'>
                        <StanFloatingMenu
                            name={`course-user-actions-${fan.userFanId}`}
                            placement='bottom-end'
                            trigger={
                                <button className='flex size-8 cursor-pointer items-center justify-center rounded-md'>
                                    <LuEllipsisVertical className='text-gray size-5' />
                                </button>
                            }
                        >
                            {({ closeMenu }) => (
                                <div className='min-w-40 py-1'>
                                    {isActive && (
                                        <>
                                            <button
                                                className='text-gray hover:bg-neutral-dimmed flex w-full items-center gap-2 px-4 py-2 text-sm'
                                                onClick={() =>
                                                    sendCourseLink(fan.slug, {
                                                        onSuccess: () => {
                                                            stanNotify({
                                                                type: 'success',
                                                                title: 'Success',
                                                                text: 'Course link has been sent',
                                                            });
                                                        },
                                                        onError: error => {
                                                            stanNotify({
                                                                type: 'error',
                                                                title: 'Error sending course link',
                                                                text: error.message,
                                                            });
                                                        },
                                                    })
                                                }
                                            >
                                                Send Course Link
                                            </button>
                                            <button
                                                className='text-gray hover:bg-neutral-dimmed flex w-full items-center gap-2 px-4 py-2 text-sm'
                                                onClick={() =>
                                                    generateCourseLink(fan.slug, {
                                                        onSuccess: data => {
                                                            copyToClipboard(data, {
                                                                successMessage: 'Course link copied to clipboard',
                                                                errorMessage: 'Failed to copy course link',
                                                            });
                                                        },
                                                        onError: error => {
                                                            stanNotify({
                                                                type: 'error',
                                                                title: 'Error copying course link',
                                                                text: error.message,
                                                            });
                                                        },
                                                    })
                                                }
                                            >
                                                Copy Course Link
                                            </button>
                                            <button
                                                className='text-negative-active hover:bg-negative-dimmed flex w-full items-center gap-2 px-4 py-2 text-sm'
                                                onClick={() => {
                                                    revokeCourseAccess(fan.userFanId, {
                                                        onSuccess: () => {
                                                            stanNotify({
                                                                type: 'success',
                                                                title: 'Success',
                                                                text: 'Course access has been revoked',
                                                            });
                                                        },
                                                        onError: error => {
                                                            stanNotify({
                                                                type: 'error',
                                                                title: 'Error revoking access',
                                                                text: error.message,
                                                            });
                                                        },
                                                    });
                                                    closeMenu();
                                                }}
                                            >
                                                Revoke Access
                                            </button>
                                        </>
                                    )}
                                    {isRevoked && (
                                        <button
                                            className='text-gray hover:bg-neutral-dimmed flex w-full items-center gap-2 px-4 py-2 text-sm'
                                            onClick={() => {
                                                restoreCourseAccess(fan.userFanId, {
                                                    onSuccess: () => {
                                                        stanNotify({
                                                            type: 'success',
                                                            title: 'Success',
                                                            text: 'Course access has been restored',
                                                        });
                                                        onRefresh();
                                                    },
                                                    onError: error => {
                                                        stanNotify({
                                                            type: 'error',
                                                            title: 'Error restoring access',
                                                            text: error.message,
                                                        });
                                                    },
                                                });
                                                closeMenu();
                                            }}
                                        >
                                            Restore Access
                                        </button>
                                    )}
                                </div>
                            )}
                        </StanFloatingMenu>
                    </div>
                );
            },
        },
    ];
};
